import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import AIMaturityAssessment from '../components/AIMaturityAssessmentCard';
import { red } from '@mui/material/colors';
import {
  AppCheckBox,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as ChevronDown } from 'assets/images/ic_chevron_down.svg';
import PageHeader from 'shared/components/PageHeader';
import { AIMaturityService } from 'services/AIMaturityService';
import { Toast, Utils } from 'shared/helpers';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import { IAppState } from 'state/RootReducer';
import { useSelector } from 'react-redux';
import useSize from 'shared/hooks/useSize';

export interface IChecklistResponse {
  _id: string;
  organizationId: string;
  checklist_item_id: string;
  is_completed: boolean;
  completed_by: string;
  completed_at: string;
  notes: string | undefined;
  editNote: string | undefined;
  created_at: string;
  updated_at: string;
  __v: number;
}

export interface IMaturityChecklist {
  _id: string;
  stage_id: string;
  item_text: string;
  item_description: any;
  sort_order: number;
  created_at: string;
  updated_at: string;
  isActive: boolean;
  checkResponse?: IChecklistResponse;
  showNotesEdit: boolean;
}

export interface IMaturityStage {
  _id: string;
  stage_number: number;
  stage_name: string;
  stage_description: string;
  created_at: string;
  updated_at: string;
  isActive: boolean;
  isExpanded: boolean;
  checkList: IMaturityChecklist[];
}

const AIMaturity = (): ReactElement => {
  const { isMobile } = useSize();
  const [maturityStageList, setMaturityStageList] = useState<IMaturityStage[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [checklistToUpdated, setChecklistToUpdated] =
    useState<IMaturityChecklist>();
  const [updatingContent, setUpdatingContent] = useState('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // const totalChecklist = useMemo(
  //   () =>
  //     maturityStageList.reduce(
  //       (prev, current) => prev + current.checkList.length,
  //       0
  //     ),
  //   [maturityStageList]
  // );
  // const totalChecklistCompleted = useMemo(
  //   () =>
  //     maturityStageList.reduce(
  //       (prev, current) =>
  //         prev +
  //         current.checkList.filter((c) => c.checkResponse?.is_completed).length,
  //       0
  //     ),
  //   [maturityStageList]
  // );
  // const totalPercent = useMemo(() => {
  //   const percent = (totalChecklistCompleted / totalChecklist) * 100;
  //   return parseInt(`${percent}`, 10);
  // }, [totalChecklist, totalChecklistCompleted]);

  // Instance Methods
  const stageProgress = (maturityStageList: IMaturityStage): string => {
    const totalChecklist = maturityStageList.checkList.length;
    const totalChecked = maturityStageList.checkList.filter(
      (c) => c.checkResponse?.is_completed
    ).length;
    let totalPercent = (totalChecked / totalChecklist) * 100;
    totalPercent = parseInt(`${totalPercent}`, 10);
    return `${totalPercent}% (${totalChecked}/${totalChecklist})`;
  };

  // API Calls
  const updateChecklist = (payload: any, callback: any): void => {
    AIMaturityService.updateChecklist(payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };
  const loadMaturityStagesAndCheckList = (): void => {
    const promises = [
      AIMaturityService.getStages(),
      AIMaturityService.getChecklist(),
      AIMaturityService.getChecklistResponse(),
    ];
    setIsLoading(true);
    Promise.all(promises)
      .then((responses) => {
        const { data: stages } = responses[0].data;
        const { data: checkList } = responses[1].data;
        const { data: checkListResponse } = responses[2].data;

        const stageList = stages.map((d: IMaturityStage) => {
          let checks: IMaturityChecklist[] = checkList.filter(
            (c: any) => c.stage_id === d._id
          );
          checks = checks.sort((a, b) => a.sort_order - b.sort_order);
          const checkResponses = checkListResponse.map((c: any) => ({
            ...c,
            editNote: c.notes,
          }));
          checks = checks.map((c) => {
            const checkResponse = checkResponses.find(
              (cr: any) => cr.checklist_item_id === c._id
            );
            return {
              ...c,
              checkResponse,
              showNotesEdit: false,
            };
          });
          return {
            ...d,
            isExpanded: false,
            checkList: checks,
          };
        });
        console.log(stageList);
        setMaturityStageList(stageList);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  // UI Actions
  const actionToggleNotes = (
    sIndex: number,
    cIndex: number,
    toggle: boolean
  ): void => {
    maturityStageList[sIndex].checkList[cIndex].showNotesEdit = toggle;
    setMaturityStageList([...maturityStageList]);
  };
  const actionOnChangeNotes = (
    sIndex: number,
    cIndex: number,
    value: string
  ): void => {
    if (!maturityStageList[sIndex].checkList[cIndex].checkResponse) {
      maturityStageList[sIndex].checkList[cIndex].checkResponse = {
        editNote: value,
      } as any;
    } else {
      maturityStageList[sIndex].checkList[cIndex].checkResponse!.editNote =
        value;
    }
    setMaturityStageList(JSON.parse(JSON.stringify(maturityStageList)));
  };

  const actionOnCancelEdit = (
    checkList: IMaturityChecklist,
    sIndex: number,
    cIndex: number
  ): void => {
    maturityStageList[sIndex].checkList[cIndex].checkResponse!.editNote =
      checkList.checkResponse!.notes;
    maturityStageList[sIndex].checkList[cIndex].showNotesEdit = false;
    setMaturityStageList(JSON.parse(JSON.stringify(maturityStageList)));
    setChecklistToUpdated(undefined);
  };
  const actionOnUpdateChecklist = (
    checkList: IMaturityChecklist,
    status: boolean,
    notes: string | undefined,
    sIndex: number,
    cIndex: number,
    updating: string
  ): void => {
    setChecklistToUpdated(checkList);
    setUpdatingContent(updating);
    const payload = {
      checklist_item_id: checkList._id,
      is_completed: status,
      notes: notes || null,
    };
    updateChecklist(payload, (response: any) => {
      if (typeof response === 'object') {
        const { data } = response.data;
        maturityStageList[sIndex].checkList[cIndex].checkResponse = {
          ...data,
          editNote: data.notes,
        };
        maturityStageList[sIndex].checkList[cIndex].showNotesEdit = false;
        setMaturityStageList(JSON.parse(JSON.stringify(maturityStageList)));
        window.postMessage(BROADCAST_MESSAGES.RELOAD_ASSESSMENTS);
        if (updating === 'notes') {
          Toast.showInfoMessage('Your notes have been saved successfully', {
            toastId: 'notes',
          });
        }
        if (updating === 'checkbox') {
          Toast.showInfoMessage(
            `Item marked as ${status ? 'completed' : 'incomplete'}`,
            { toastId: `checkbox_${status}` }
          );
        }
      }
      setChecklistToUpdated(undefined);
    });
  };

  // Hooks
  useEffect(() => {
    loadMaturityStagesAndCheckList();
  }, []);

  // UI Elements
  return (
    <>
      <PageHeader
        title="AI Maturity Assessment"
        description="Assess and track your organisation's AI governance maturity across 5 key stages."
      />
      <Box className="dashboard-page ai-maturity">
        <HeadTag title="AI Maturity" />

        {isLoading && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <Box className="dashboard-wrapper">
            <Box className="maturity-assessment">
              <Grid container className="maturity-progress-content">
                <Grid item md={4} xs={12}>
                  <AIMaturityAssessment minimize />
                </Grid>
                <Grid item md={8} xs={12}>
                  <Card variant="outlined">
                    <CardHeader
                      title="AI maturity assessment"
                      subheader="Evaluate your organisation's AI governance and risk management maturity. Complete the checklist items for each stage to track your progress and identify areas for improvement."
                    />
                  </Card>
                </Grid>
              </Grid>
            </Box>
            <Box className="management-checklist">
              {maturityStageList.map((stage, sIndex) => (
                <Card
                  key={stage._id}
                  variant="outlined"
                  className={stage.isExpanded ? 'open mb-5' : 'closed mb-5'}
                  sx={{
                    backgroundColor: stageProgress(stage).startsWith('100')
                      ? '#f7ffe9'
                      : '#fff',
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {/* Stage Header */}
                  <CardActionArea
                    onClick={() => {
                      maturityStageList[sIndex].isExpanded = !stage.isExpanded;
                      setMaturityStageList([...maturityStageList]);
                    }}
                  >
                    <CardHeader
                      sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 2, sm: 2.5 },

                        '& .MuiCardHeader-content': {
                          minWidth: 0,
                        },

                        '& .MuiCardHeader-title': {
                          minWidth: 0,
                        },

                        '& .MuiCardHeader-subheader': {
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        },

                        '& .MuiCardHeader-avatar': {
                          mr: { xs: 1.5, sm: 2 },
                        },

                        '& .MuiCardHeader-action': {
                          margin: 0,
                          alignSelf: 'center',
                          flexShrink: 0,
                        },
                      }}
                      avatar={
                        <Avatar
                          sx={{
                            bgcolor: red[500],
                            width: { xs: 50, sm: 50 },
                            height: { xs: 50, sm: 50 },
                            fontSize: { xs: 20, sm: 20 },
                            flexShrink: 0,
                          }}
                          aria-label="stage"
                        >
                          {sIndex + 1}
                        </Avatar>
                      }
                      action={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={{ xs: 0.5, sm: 2 }}
                          sx={{
                            flexShrink: 0,
                          }}
                        >
                          {stageProgress(stage).startsWith('100') && (
                            <CheckCircleOutlineIcon
                              color="primary"
                              sx={{
                                fontSize: { xs: 22, sm: 24 },
                              }}
                            />
                          )}

                          <Chip
                            size="small"
                            color={
                              stageProgress(stage).startsWith('100')
                                ? 'primary'
                                : 'default'
                            }
                            label={stageProgress(stage)}
                            sx={{
                              fontSize: { xs: 12, sm: 13 },
                              height: { xs: 28, sm: 32 },
                            }}
                          />

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();

                              maturityStageList[sIndex].isExpanded =
                                !stage.isExpanded;

                              setMaturityStageList([...maturityStageList]);
                            }}
                          >
                            <SvgIcon
                              component={ChevronDown}
                              inheritViewBox
                              className="icon-drop-down-color down"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                              sx={{
                                fontSize: { xs: 22, sm: 26 },
                              }}
                            />
                          </IconButton>
                        </Stack>
                      }
                      title={
                        <Typography
                          className="f20 fw-6"
                          sx={{
                            fontSize: { xs: 17, sm: 20 },
                            lineHeight: { xs: 1.2, sm: 1.3 },
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {stage.stage_name}
                        </Typography>
                      }
                      subheader={
                        <Typography
                          variant="body1"
                          sx={{
                            mt: { xs: 0.5, sm: 1 },
                            fontSize: { xs: 14, sm: 16 },
                            lineHeight: { xs: 1.5, sm: 1.6 },
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {stage.stage_description}
                        </Typography>
                      }
                    />
                  </CardActionArea>

                  <Collapse in={stage.isExpanded} timeout="auto" unmountOnExit>
                    <CardContent
                      sx={{
                        px: { xs: 2, sm: 2.5 },
                        pt: { xs: 0.5, sm: 1 },
                        pb: { xs: 2, sm: 2.5 },
                      }}
                    >
                      {stage.checkList.map((checkList, cIndex) => (
                        <Stack
                          key={checkList._id}
                          className="app-box-border mb-2"
                          direction="row"
                          spacing={isMobile ? 1 : 2}
                          alignItems="flex-start"
                          sx={{
                            p: isMobile ? 1.5 : 2,
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          {checklistToUpdated?._id === checkList._id &&
                          updatingContent === 'checkbox' ? (
                            <CircularProgress size={18} className="mr-2 ml-2" />
                          ) : (
                            <AppCheckBox
                              disabled={
                                !orgACLConfig.dashboard.includes(
                                  'dashboard.aiMaturity.checkbox'
                                )
                              }
                              checked={
                                checkList.checkResponse?.is_completed ?? false
                              }
                              onChange={(e: any) => {
                                actionOnUpdateChecklist(
                                  checkList,
                                  e.target.checked,
                                  checkList.checkResponse?.notes,
                                  sIndex,
                                  cIndex,
                                  'checkbox'
                                );
                              }}
                            />
                          )}

                          <Stack flexGrow={1}>
                            <Typography className="body1">
                              {checkList.item_text}ss
                            </Typography>

                            {checkList.checkResponse?.notes && (
                              <Typography
                                className="text-gray"
                                sx={{
                                  fontSize: { xs: 14, sm: 16 },
                                  lineHeight: 1.5,
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                }}
                              >
                                Notes: {checkList.checkResponse?.notes}
                              </Typography>
                            )}
                            {orgACLConfig.dashboard.includes(
                              'dashboard.aiMaturity.note'
                            ) && (
                              <>
                                {!checkList.showNotesEdit && (
                                  <Stack direction="row" className="mt-2">
                                    <AppPrimaryButtonOutlined
                                      title={
                                        checkList.checkResponse?.notes
                                          ? 'Edit Note'
                                          : 'Add Note'
                                      }
                                      className="btn-extra-small mt-2"
                                      onClick={() => {
                                        actionToggleNotes(sIndex, cIndex, true);
                                      }}
                                    />
                                  </Stack>
                                )}
                                {checkList.showNotesEdit && (
                                  <Stack className="mt-2">
                                    <TextField
                                      fullWidth
                                      value={
                                        checkList.checkResponse?.editNote ?? ''
                                      }
                                      onChange={(e) => {
                                        const { value } = e.target;
                                        actionOnChangeNotes(
                                          sIndex,
                                          cIndex,
                                          value
                                        );
                                      }}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Stack direction="row" spacing={2}>
                                      <AppPrimaryButton
                                        className="btn-extra-small"
                                        title="Save"
                                        disabled={
                                          checklistToUpdated?._id ===
                                          checkList._id
                                        }
                                        isLoading={
                                          checklistToUpdated?._id ===
                                            checkList._id &&
                                          updatingContent === 'notes'
                                        }
                                        loadingText="Saving"
                                        onClick={() => {
                                          actionOnUpdateChecklist(
                                            checkList,
                                            checkList.checkResponse
                                              ?.is_completed ?? false,
                                            checkList.checkResponse?.editNote,
                                            sIndex,
                                            cIndex,
                                            'notes'
                                          );
                                        }}
                                      />
                                      <AppPrimaryButtonOutlined
                                        className="btn-extra-small"
                                        title="Cancel"
                                        disabled={
                                          checklistToUpdated?._id ===
                                          checkList._id
                                        }
                                        onClick={() => {
                                          actionOnCancelEdit(
                                            checkList,
                                            sIndex,
                                            cIndex
                                          );
                                        }}
                                      />
                                    </Stack>
                                  </Stack>
                                )}
                              </>
                            )}
                          </Stack>
                        </Stack>
                      ))}
                    </CardContent>
                  </Collapse>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AIMaturity;
